'use client'

// Mailbox editor surface: the skin tokens byte-replicate the pre-skinification
// class strings (behaviour-neutrality proof — the rendered class attribute is
// unchanged). Restyle the mailbox editor by editing THESE tokens.

import { EditorSurfaceProvider, type EditorSkin, type EditorSurfaceValue } from '@/deskUiSkeletons/editor/EditorSurfaceContext'
import { settingsClasses } from './components/Settings/Settings.classes'
import { MAILBOX_RICH_EDIT_VARIANT, RICH_EDIT_POLICY, mailboxRichEditSkin } from './richEdit.config'
import type { ReactNode } from 'react'

const settings = settingsClasses('mailbox')

export const mailboxEditorSkin: EditorSkin = {
  headShort: '!hidden',
  head: {
    root: 'flex flex-col border-b border-[#d9e1e8] bg-white px-5 py-2',
    nameKeyRow: 'flex min-w-0 items-start gap-3 border-b border-[#edf1f4] py-2',
    fromLabel: 'w-16 flex-none pt-1.5 text-[12px] font-medium text-[#687684]',
    recipientsRow: 'flex min-w-0 items-center border-b border-[#edf1f4] py-2',
    subjectRow: 'flex min-w-0 items-center gap-3 py-2',
    subjectLabel: 'w-16 flex-none text-[12px] font-medium text-[#687684]',
    subjectInputBox: 'min-w-0 flex-1 bg-white',
    subjectInput: '!m-0 block !w-full box-border !rounded-none !border-0 !bg-white !px-0 !py-[5px] text-[14px] text-[#17212b] shadow-none outline-none placeholder:text-[#9aa5b1]',
    compatControl: 'hidden pointer-events-none',
  },
  nameKey: {
    block: 'flex min-w-0 flex-1 flex-col gap-1.5',
    moreButton: 'w-fit text-[10px] font-medium text-[#687684] hover:text-[#1556a8] hover:underline',
    advancedOpen: 'block',
    advancedClosed: 'hidden',
  },
  smartName: {
    root: 'flex w-full min-w-0 items-center gap-1.5 text-xs font-[Arial,Tahoma,Verdana,sans-serif]',
    sign: 'box-border h-[23px] flex-[0_0_auto] cursor-default border-b border-[#d3d3d3] bg-[#ffffe0] px-[5px] pt-1.5 text-[#888] !flex !h-8 !w-8 items-center justify-center !rounded-l !border !border-r-0 !border-[#c8d2dc] !bg-[#f5f8fb] !p-0 text-[#687684]',
    signStatus: { busy: 'text-[#222]', ok: 'text-green-700', wrong: 'text-red-600' },
    inputBox: 'relative flex h-7 min-w-[235px] flex-1 items-center overflow-hidden border-b border-[#d3d3d3] bg-[#ffffe0] !h-8 !min-w-0 !border !border-l-0 !border-[#c8d2dc] !bg-white',
    inputBoxWrong: 'border-b-[#c00]',
    input: '!m-0 h-7 !w-full !rounded-none !border-0 !bg-white !px-2 !py-0 text-[13px] text-[#222] shadow-none outline-none placeholder:text-[#9aa5b1] font-[Arial,sans-serif]',
    actions: 'group relative box-border h-[22px] flex-[0_0_auto] py-[3px] pl-0.5 pr-1 !h-8 !p-0',
    check: 'block cursor-pointer rounded-[5px] p-[3px] text-[#3472a9] hover:underline',
    quick: 'absolute left-0 top-full z-[9999999] hidden group-hover:block group-focus-within:block',
    quickItems: 'w-[150px] bg-[#d3d3d3] p-0.5 text-xs',
    quickItem: 'mb-0.5 inline-block cursor-pointer bg-[#f5f5f5] p-0.5 text-black',
  },
  extName: {
    root: 'ml-[7px] flex flex-col gap-1 text-xs font-[Arial,Tahoma,Verdana,sans-serif]',
    quickRow: 'flex flex-wrap items-center gap-1',
    label: 'p-[3px] text-[#3472a9]',
    quickBtn: 'cursor-pointer bg-[#f5f5f5] p-0.5 text-xs text-black hover:underline',
    subKeyBtn: 'cursor-pointer bg-[#3472a9] p-0.5 text-xs text-white hover:underline',
    box: 'relative max-w-[80%] overflow-hidden border-b border-[#d3d3d3] bg-[#ffffe0] pr-[30px]',
    textarea: '!m-0 block box-border min-h-[21px] !w-full resize-none !rounded-none !border-0 !bg-[#ffffe0] !p-[5px] !pb-0 text-[#222] outline-none text-[0.875rem] leading-[150%] font-[Arial,sans-serif]',
    mirror: 'hidden',
    checkRow: 'flex flex-wrap items-center gap-1.5',
    subKeyText: 'block bg-[#3472a9] p-0.5 text-xs text-white empty:hidden',
    check: 'cursor-pointer p-0.5 text-xs text-[#3472a9] hover:underline',
    emailInfo: 'cursor-default p-0.5 text-[11px] text-[#888]',
    emailEdit: 'cursor-pointer p-0.5 text-[11px] text-[#3472a9] underline',
    code: 'hidden flex-wrap items-center gap-2 text-xs',
    codeInput: 'w-[250px] text-right text-xs',
    codeBtn: 'cursor-pointer p-0.5 text-[#3472a9]',
    codeInfo: 'text-[11px] text-[#888]',
  },
  recipients: {
    root: 'flex w-full items-center gap-1.5 text-xs font-[Arial,Tahoma,Verdana,sans-serif]',
    label: 'flex-[0_0_auto] text-[#888] text-[0.875rem] font-[Arial,sans-serif]',
    field: 'flex min-w-0 flex-1 flex-wrap items-center gap-1 overflow-hidden border-b border-[silver] bg-white pr-[30px]',
    inputWrap: 'min-w-[60px] flex-1 overflow-hidden',
    input: '!mt-0.5 block !w-full !rounded-none !border-0 !bg-transparent !p-[5px] text-[#222] shadow-none outline-none text-[0.875rem] font-[Arial,sans-serif]',
    inputErr: '!text-[#b91c1c] placeholder:text-[#b91c1c]',
    inputErrHint: 'flex-none self-center whitespace-nowrap pr-2 text-[11px] text-[#b91c1c]',
    list: 'my-[3px] ml-[5px] flex flex-wrap items-center gap-1',
    token: 'flex h-5 cursor-pointer items-center rounded-[7px] px-2 leading-5 text-[#404040]',
    tokenKind: { email: 'bg-[lightblue]', msg: 'bg-[wheat]', self: 'bg-[#3472a9]' },
    tokenText: 'max-h-5 text-xs font-bold tracking-[0.3px] text-[#3472a9] font-[Arial,sans-serif]',
    tokenTextSelf: 'text-white',
    tokenX: 'ml-[5px] cursor-pointer font-bold text-[#888] hover:text-red-600',
    tokenXSelf: 'text-[#d3d3d3]',
    tokenLocked: '!cursor-default',
    compat: 'hidden',
  },
  settings: {
    root: 'grid min-h-0 flex-1 grid-cols-[220px_minmax(0,1fr)] overflow-hidden bg-white text-left text-[13px] text-[#314253] max-[700px]:grid-cols-1 max-[700px]:grid-rows-[auto_minmax(0,1fr)]',
    tabs: 'flex min-h-0 flex-col gap-1 overflow-y-auto border-r border-[#d9e1e8] bg-[#f5f7fa] p-4 max-[700px]:flex-row max-[700px]:overflow-x-auto max-[700px]:border-b max-[700px]:border-r-0 max-[700px]:p-2',
    tab: 'min-h-10 flex-none cursor-pointer rounded border-0 px-3 py-2 text-left text-[13px]',
    tabActive: 'bg-white font-semibold text-[#1556a8] shadow-sm',
    tabInactive: 'bg-transparent text-[#526272] hover:bg-white/80',
    body: 'min-h-0 min-w-0 overflow-auto p-8 max-[700px]:p-5',
    paneHidden: 'hidden',
    panel: settings.panel,
    legend: settings.legend,
    row: settings.row,
    check: settings.check,
    rowCheck: settings.rowCheck,
    field: settings.field,
    combo: settings.combo,
    textInput: settings.textInput,
    timeRow: 'mb-[7px] flex flex-col gap-[3px]',
    timeInput: 'box-border w-full cursor-pointer border border-[#a9a9a9] bg-[#f4f5f6] p-1 text-xs font-[Arial,sans-serif]',
    avatarButton: 'flex w-full cursor-pointer items-center gap-2 border border-dashed border-[#aab5bf] bg-[#f8fafb] p-1.5 text-xs text-[#3472a9] font-[Arial,sans-serif]',
    avatarImage: 'h-16 w-16 border border-[#c9cdd6] bg-white object-cover',
    avatarInput: 'hidden',
    protectForm: 'contents',
  },
  body: {
    root: 'flex min-h-0 flex-1 flex-col bg-white',
    tools: 'flex min-h-11 flex-wrap items-center gap-1 border-b border-[#d9e1e8] bg-[#f8fafb] px-4 py-1.5',
    toolButton: 'cursor-pointer rounded border bg-white px-2 py-[5px] border-[#c8d2dc] text-[12px] font-medium text-[#314253] hover:bg-[#edf2f6]',
    toolButtonActive: 'cursor-pointer rounded border px-2 py-[5px] border-[#2b6cb0] bg-[#e8f1fa] text-[12px] font-medium text-[#2b6cb0]',
    legacyToolbarHidden: '!hidden',
    richFoldWrap: 'flex min-w-0 flex-wrap items-center gap-1 text-[11px] text-[#526272]',
    richFoldButton: 'h-7 rounded px-2 hover:bg-white',
    textWrap: 'min-h-0 flex-1',
    textInner: 'h-full min-h-[360px]',
    textarea: 'box-border h-full min-h-[360px] w-full resize-none border-0 bg-white px-6 py-5 text-[14px] leading-6 text-[#26323d] outline-none placeholder:text-[#9aa5b1]',
    richSurface: 'box-border h-full min-h-[360px] w-full overflow-auto border-0 bg-white px-6 py-5 text-[14px] leading-6 text-[#26323d] outline-none',
    richToolbar: 'flex min-w-0 flex-wrap items-center gap-1',
    richButton: 'h-7 min-w-7 cursor-pointer rounded border border-[#c8d2dc] bg-white px-1.5 text-[12px] leading-none text-[#314253] hover:bg-[#edf2f6] active:bg-[#e2e9ef]',
    editPinWarning: 'mx-4 mb-3 rounded border border-[#e5b94f] bg-[#fff8df] px-3 py-2 text-[12px] text-[#765b12]',
  },
}

const mailboxEditorSurface: EditorSurfaceValue = {
  variant: 'mailbox',
  skin: mailboxEditorSkin,
  richEditVariant: MAILBOX_RICH_EDIT_VARIANT,
  richEditPolicy: RICH_EDIT_POLICY,
  richEditSkin: mailboxRichEditSkin,
  richEditDefaultFormat: 'model',
}

export function MailboxEditorSurfaceProvider({ children }: { children: ReactNode }) {
  return <EditorSurfaceProvider value={mailboxEditorSurface}>{children}</EditorSurfaceProvider>
}
